import { HashLoader } from "react-spinners";
import useLoans from "../../hooks/useInstallements";
import { Link } from "react-router-dom";
import LoanItem from "../../components/LoanItem";
import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

type TabId = "pending" | "active" | "history";

const Plans = () => {
  const { installments, fetchingInstallments } = useLoans();

  const [activeTab, setActiveTab] = useState<TabId>("pending");

  const { pending, active, history } = useMemo(() => {
    if (!installments?.data) {
      return { pending: [], active: [], history: [] };
    }
    const pendingList: typeof installments.data = [];
    const activeList: typeof installments.data = [];
    const historyList: typeof installments.data = [];

    for (const installment of installments.data) {
      const paidCount = installment?.installments?.filter((i) => i.paid).length ?? 0;
      const isCompleted =
        installment.number_of_installments > 0 &&
        paidCount >= installment.number_of_installments;
      const isDeclined = (installment.loan_status ?? "").toLowerCase() === "declined";
      const isPending = (installment.loan_status ?? "").toLowerCase() === "pending";

      if (isPending) {
        pendingList.push(installment);
      } else if (isCompleted || isDeclined) {
        historyList.push(installment);
      } else {
        activeList.push(installment);
      }
    }

    const sortByAmount = (a: typeof installments.data[0], b: typeof installments.data[0]) =>
      b.total_amount - a.total_amount;
    pendingList.sort(sortByAmount);
    activeList.sort(sortByAmount);
    historyList.sort((a, b) => {
      const aPaid = a.installments?.filter((i) => i.paid).length ?? 0;
      const bPaid = b.installments?.filter((i) => i.paid).length ?? 0;
      if (aPaid !== bPaid) return bPaid - aPaid;
      return b.total_amount - a.total_amount;
    });

    return { pending: pendingList, active: activeList, history: historyList };
  }, [installments]);

  const filteredInstallments = useMemo(() => {
    if (activeTab === "pending") return pending;
    if (activeTab === "active") return active;
    return history;
  }, [activeTab, pending, active, history]);

  return (
    <div className="relative w-full flex gap-5 px-5 md:px-10 py-5 flex-col">
      <h3 className="text-2xl font-medium">
        Pay in 3 - Installment payments made easy
      </h3>
      <div className="bg-gray-100 p-1.5 rounded-md max-w-[28rem] flex items-center gap-1">
        <AnimatePresence>
          {(
            [
              { id: "pending" as TabId, label: "Pending", count: pending.length },
              { id: "active" as TabId, label: "Active", count: active.length },
              { id: "history" as TabId, label: "History", count: history.length },
            ] as const
          ).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              type="button"
              className="rounded flex items-center text-center px-4 py-2 gap-2 cursor-pointer flex-1 justify-center relative min-w-0"
            >
              {activeTab === tab.id && (
                <motion.div
                  layoutId="pill"
                  className="bg-white rounded absolute top-0 left-0 w-full h-full shadow-sm"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.4 }}
                />
              )}
              <div className="flex items-center justify-center gap-2 relative z-[1]">
                <span>{tab.label}</span>
                {tab.count > 0 && (
                  <div className="bg-red-500 rounded-full text-sm text-white leading-none min-w-[1.5rem] h-6 px-1.5 flex items-center justify-center">
                    {tab.count}
                  </div>
                )}
              </div>
            </button>
          ))}
        </AnimatePresence>
      </div>
      <div>
        {fetchingInstallments ? (
          <div className="grid place-items-center p-20">
            <HashLoader size={40} />
          </div>
        ) : (
          <div>
            {filteredInstallments?.length ? (
              <div className="flex flex-col gap-4">
                {filteredInstallments?.map((installment) => {
                  return <LoanItem key={installment.id} installment={installment} />;
                })}
              </div>
            ) : (
              <div className="flex flex-col text-center justify-center p-10 items-center gap-5">
                <img
                  src={require("../../assets/images/payment-empty.png")}
                  alt="empty"
                  className="w-72"
                />
                <div className="flex flex-col text-center justify-center items-center">
                  <div className="text-visaro-orange font-medium text-lg">
                    {activeTab === "pending" ? "No pending loan requests." : activeTab === "active" ? "No active repayment plans." : "No plan history yet."}
                  </div>
                  <div className="text-gray-500">
                    Select one of the services on your dashboard to get started
                    with Visaro BNPL
                  </div>
                </div>
                <div className="flex md:flex-row flex-col md:justify-between gap-5 items-center">
                  <Link
                    to="/home"
                    className="p-2 py-3 text-center bg-deepBlue text-white px-10 ring-2 ring-deepBlue/10 rounded-xl font-medium"
                  >
                    Return to dashboard
                  </Link>
                  <Link
                    to="/help"
                    className="p-2 py-3 text-center bg-white text-gray-950 px-6 ring-2 ring-gray-100/50 rounded-xl font-medium flex gap-2 items-center"
                  >
                    How to use Visaro BNPL
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Plans;
